(() => {
  "use strict";

  const root = document.querySelector("[data-customizer-root]");
  if (!root) return;

  const apiBase = document.body.dataset.customizationApi.replace(/\/+$/, "");
  const isLocalDemo = ["127.0.0.1", "localhost"].includes(window.location.hostname);
  const supportedLocales = new Set(["en-US", "de-DE", "it-IT", "es-ES", "fr-FR"]);
  const requestedLocale = localStorage.getItem("formOfUsLocale") || document.documentElement.lang || "en-US";
  const locale = supportedLocales.has(requestedLocale) ? requestedLocale : "en-US";
  const handle = decodeURIComponent(location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || new URLSearchParams(location.search).get("handle") || "custom-funko-figurine-from-photo");

  const nodes = {
    title: document.querySelector("[data-product-title]"),
    breadcrumb: document.querySelector("[data-product-breadcrumb]"),
    category: document.querySelector("[data-product-category]"),
    image: document.querySelector("[data-product-image]"),
    thumb: document.querySelector("[data-product-thumb]"),
    price: document.querySelector("[data-product-price]"),
    compare: document.querySelector("[data-product-compare]"),
    styles: document.querySelector("[data-style-options]"),
    selectedStyle: document.querySelector("[data-selected-style]"),
    selectedSize: document.querySelector("[data-selected-size]"),
    input: document.querySelector("[data-photo-input]"),
    sourcePreview: document.querySelector("[data-source-preview]"),
    uploadCopy: document.querySelector("[data-upload-copy]"),
    note: document.querySelector("[data-customer-note]"),
    turnstileWrap: document.querySelector("[data-turnstile-wrap]"),
    turnstileWidget: document.querySelector("[data-turnstile-widget]"),
    status: document.querySelector("[data-customizer-status]"),
    generate: document.querySelector("[data-generate-button]"),
    cartButton: document.querySelector("[data-cart-button]"),
    dialog: document.querySelector("[data-preview-dialog]"),
    previewGrid: document.querySelector("[data-preview-grid]"),
    stageImage: document.querySelector("[data-stage-image]"),
    stageLoader: document.querySelector("[data-stage-loader]"),
    stageEmpty: document.querySelector("[data-stage-empty]"),
    stageLabel: document.querySelector("[data-stage-label]"),
    stageStatus: document.querySelector("[data-stage-status]"),
    stageRetry: document.querySelector("[data-stage-retry]"),
    confirm: document.querySelector("[data-confirm-selection]"),
    historyDialog: document.querySelector("[data-history-dialog]"),
    historyList: document.querySelector("[data-history-list]"),
  };

  const state = {
    product: null,
    dictionary: null,
    file: null,
    sourceObjectUrl: "",
    sessionId: "",
    sessionToken: "",
    tasks: [],
    selectedTaskId: "",
    activeSlot: 1,
    batchKey: "",
    retryKeys: {},
    pollTimer: null,
    turnstileToken: "",
    turnstileWidgetId: "",
  };

  const money = (value) => new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

  const themeAsset = (name) => window.FORM_OF_US_THEME_ASSETS?.[name] || `/assets/${name}`;
  const stableKey = (prefix) => `${prefix}:${crypto.randomUUID()}`;
  const getNested = (object, key) => key.split(".").reduce((current, part) => current && current[part], object);
  const copy = (key, fallback) => getNested(state.dictionary, key) || fallback;
  const optionLabel = (slot) => copy(`preview.option_${slot}`, `Option ${slot}`);
  const statusLabel = (status) => copy(`preview.status_${status}`, String(status || "waiting").replace(/_/g, " "));

  function applyLocaleCopy() {
    document.documentElement.lang = locale;
    document.querySelectorAll("[data-i18n]").forEach((node) => {
      const value = getNested(state.dictionary, node.dataset.i18n);
      if (typeof value === "string") node.textContent = value;
    });
    nodes.note.placeholder = copy("product.note_placeholder", nodes.note.placeholder);
  }

  async function loadDictionary() {
    if (window.FORM_OF_US_LOCALES?.[locale]) return window.FORM_OF_US_LOCALES[locale];
    const response = await fetch(`/locales/${encodeURIComponent(locale)}.json`);
    if (!response.ok) throw new Error("locale_load_failed");
    return response.json();
  }

  function setStatus(message, error = false) {
    nodes.status.textContent = message;
    nodes.status.classList.toggle("is-error", error);
  }

  function updateGenerateAvailability() {
    nodes.generate.disabled = !state.file || (!isLocalDemo && !state.turnstileToken);
  }

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-form-of-us-turnstile="1"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.formOfUsTurnstile = "1";
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", () => reject(new Error("Security check could not be loaded.")), { once: true });
      document.head.append(script);
    });
  }

  async function initializeTurnstile() {
    if (isLocalDemo) return;
    nodes.turnstileWrap.hidden = false;
    const config = await api("/api/shopify/customization/config");
    if (!config.turnstile_site_key) throw new Error("Security check is not configured.");
    await loadTurnstileScript();
    state.turnstileWidgetId = window.turnstile.render(nodes.turnstileWidget, {
      sitekey: config.turnstile_site_key,
      theme: "light",
      callback: (token) => {
        state.turnstileToken = token;
        updateGenerateAvailability();
        if (state.file) setStatus(copy("product.generate", "Generate four independent 2K previews when you are ready."));
      },
      "expired-callback": () => {
        state.turnstileToken = "";
        updateGenerateAvailability();
        setStatus(copy("product.error_generic", "Security check expired. Please complete it again."), true);
      },
      "error-callback": () => {
        state.turnstileToken = "";
        updateGenerateAvailability();
        setStatus(copy("product.error_generic", "Security check failed. Please reload and try again."), true);
      },
    });
    updateGenerateAvailability();
  }

  function authHeaders(idempotencyKey = "") {
    const headers = { "content-type": "application/json" };
    if (state.sessionToken) headers["x-customization-session"] = state.sessionToken;
    if (idempotencyKey) headers["x-idempotency-key"] = idempotencyKey;
    return headers;
  }

  async function api(path, options = {}) {
    const response = await fetch(apiBase + path, options);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const fallback = copy("product.error_generic", `Request failed (${response.status})`);
      throw new Error(locale === "en-US" ? payload?.error?.message || fallback : fallback);
    }
    return payload;
  }

  function saveSession() {
    if (!state.sessionId || !state.sessionToken) return;
    localStorage.setItem("formOfUsCustomization", JSON.stringify({
      sessionId: state.sessionId,
      sessionToken: state.sessionToken,
      productHandle: handle,
      createdAt: Date.now(),
    }));
  }

  function renderProduct(product) {
    state.product = product;
    const title = product.title[locale] || product.title["en-US"];
    const imageUrl = themeAsset(`product-${product.handle}-v1.png`);
    document.title = `${title} — FORM OF US`;
    nodes.title.textContent = title;
    nodes.breadcrumb.textContent = title;
    nodes.category.textContent = product.category.replace(/_/g, " ");
    nodes.image.src = imageUrl;
    nodes.image.alt = title;
    nodes.thumb.src = imageUrl;
    nodes.price.textContent = money(product.price);
    nodes.compare.textContent = money(product.compare_at_price);
    nodes.styles.innerHTML = product.style_options.map((style, index) => `
      <label>
        <input type="radio" name="style" value="${style.replace(/"/g, "&quot;")}" ${index === 0 ? "checked" : ""}>
        <span>${style}</span>
      </label>`).join("");
    nodes.selectedStyle.textContent = product.style_options[0];
  }

  function currentStyle() {
    return document.querySelector('input[name="style"]:checked')?.value || state.product.style_options[0];
  }

  function updateSlot(slotNumber, task = {}) {
    const card = nodes.previewGrid.querySelector(`[data-slot="${slotNumber}"]`);
    if (!card) return;
    const image = card.querySelector("img");
    const status = card.querySelector(".preview-slot__meta span");
    const taskStatus = task.status || "waiting";
    card.dataset.taskId = task.task_id || "";
    card.classList.toggle("is-ready", taskStatus === "completed");
    card.classList.toggle("is-failed", taskStatus === "failed");
    card.classList.toggle("selected", Boolean(task.selected) || task.task_id === state.selectedTaskId);
    status.textContent = statusLabel(taskStatus);
    if (task.preview_url) {
      image.src = task.preview_url;
      image.hidden = false;
    } else if (isLocalDemo && ["completed", "failed"].includes(taskStatus)) {
      image.src = themeAsset(`product-${handle}-v1.png`);
      image.hidden = taskStatus !== "completed";
    } else {
      image.hidden = true;
    }
  }

  function renderTasks(tasks) {
    state.tasks = [...tasks].sort((a, b) => Number(a.slot) - Number(b.slot));
    for (let slot = 1; slot <= 4; slot += 1) {
      updateSlot(slot, state.tasks.find((task) => Number(task.slot) === slot) || { slot, status: "waiting" });
    }
    const active = state.tasks.find((task) => Number(task.slot) === Number(state.activeSlot))
      || state.tasks.find((task) => task.status === "completed")
      || state.tasks[0]
      || { slot: 1, status: "waiting" };
    state.activeSlot = Number(active.slot) || 1;
    const activeStatus = active.status || "waiting";
    nodes.stageLabel.textContent = optionLabel(state.activeSlot);
    nodes.stageStatus.textContent = statusLabel(activeStatus);
    nodes.stageRetry.hidden = activeStatus !== "failed";
    nodes.stageRetry.dataset.stageRetry = String(state.activeSlot);
    nodes.stageLoader.hidden = !["waiting", "queued", "processing"].includes(activeStatus);
    if (active.preview_url || (isLocalDemo && activeStatus === "completed")) {
      nodes.stageImage.src = active.preview_url || themeAsset(`product-${handle}-v1.png`);
      nodes.stageImage.hidden = false;
      nodes.stageEmpty.hidden = true;
    } else {
      nodes.stageImage.hidden = true;
      nodes.stageEmpty.hidden = false;
      nodes.stageEmpty.textContent = activeStatus === "failed"
        ? copy("product.retry_one", "This option needs another try.")
        : copy("product.preparing", "Four independent 2K previews are being prepared.");
    }
    const selected = state.tasks.find((task) => task.selected || task.task_id === state.selectedTaskId);
    nodes.confirm.disabled = !selected;
  }

  function demoStart() {
    state.sessionId = `demo_${Date.now()}`;
    state.sessionToken = "local-demo";
    state.tasks = [1, 2, 3, 4].map((slot) => ({ task_id: `demo_task_${slot}`, slot, status: "processing", progress: 30 }));
    saveSession();
    renderTasks(state.tasks);
    setTimeout(() => {
      state.tasks = state.tasks.map((task) => ({ ...task, status: "completed", progress: 100, preview_url: `${themeAsset(`product-${handle}-v1.png`)}?slot=${task.slot}` }));
      renderTasks(state.tasks);
      setStatus(copy("preview.title", "Four local preview slots are ready."));
    }, 900);
  }

  async function createAndUploadSession() {
    const product = state.product;
    const session = await api("/api/shopify/customization/sessions", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        shop: window.Shopify?.shop || location.host,
        catalog_id: product.id,
        product_handle: product.handle,
        product_type: product.product_type,
        locale,
        content_type: state.file.type,
        file_size: state.file.size,
        turnstile_token: state.turnstileToken,
      }),
    });
    state.sessionId = session.session_id;
    state.sessionToken = session.session_token;
    await fetch(session.upload_url, {
      method: "PUT",
      headers: { "content-type": state.file.type },
      body: state.file,
    }).then((response) => {
      if (!response.ok) throw new Error("Private upload failed.");
    });
    await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/uploaded`, {
      method: "POST",
      headers: authHeaders(),
      body: "{}",
    });
    saveSession();
  }

  async function startGeneration() {
    if (!state.file || !state.product) return;
    nodes.generate.disabled = true;
    nodes.generate.textContent = `${copy("product.generate", "Generate 4 × 2K previews")}…`;
    nodes.dialog.showModal();
    renderTasks([1, 2, 3, 4].map((slot) => ({ slot, status: "queued" })));
    try {
      if (isLocalDemo) {
        demoStart();
        return;
      }
      if (!state.sessionId) await createAndUploadSession();
      state.batchKey ||= stableKey(`batch:${state.sessionId}`);
      const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/generations`, {
        method: "POST",
        headers: authHeaders(state.batchKey),
        body: JSON.stringify({
          model: "gpt-image-2-2K",
          style_id: currentStyle(),
          options: {
            customer_note: nodes.note.value.trim(),
            size: document.querySelector('input[name="size"]:checked')?.value || "6 cm",
          },
        }),
      });
      renderTasks(payload.tasks || []);
      beginPolling();
    } catch (error) {
      setStatus(error.message, true);
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Generate 4 × 2K previews");
    }
  }

  async function refreshSession() {
    let payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
      headers: authHeaders(),
    });
    const pending = (payload.tasks || []).filter((task) => !["completed", "failed"].includes(task.status));
    if (pending.length) {
      await Promise.allSettled(pending.map((task) => api(
        `/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}`,
        { headers: authHeaders() },
      )));
      payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
        headers: authHeaders(),
      });
    }
    renderTasks(payload.tasks || []);
    const terminal = (payload.tasks || []).every((task) => ["completed", "failed"].includes(task.status));
    if (terminal) {
      clearInterval(state.pollTimer);
      state.pollTimer = null;
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Generate four new previews");
      setStatus(payload.status === "partial_failed" ? copy("product.retry_one", "Some previews need another try.") : copy("preview.title", "Four previews are ready."));
    }
  }

  function beginPolling() {
    clearInterval(state.pollTimer);
    refreshSession().catch(() => {});
    state.pollTimer = setInterval(() => refreshSession().catch((error) => setStatus(error.message, true)), 2500);
  }

  async function retrySlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task) return;
    if (isLocalDemo) {
      const updated = state.tasks.map((candidate) => Number(candidate.slot) === Number(slot) ? { ...candidate, status: "completed" } : candidate);
      renderTasks(updated);
      return;
    }
    state.retryKeys[task.task_id] ||= stableKey(`retry:${task.task_id}`);
    const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}/retry`, {
      method: "POST",
      headers: authHeaders(state.retryKeys[task.task_id]),
      body: "{}",
    });
    renderTasks(state.tasks.map((candidate) => candidate.task_id === task.task_id ? payload.task || payload : candidate));
    beginPolling();
  }

  async function selectSlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task || task.status !== "completed") return;
    state.activeSlot = Number(slot);
    if (!isLocalDemo) {
      await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/select`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ task_id: task.task_id }),
      });
    }
    state.selectedTaskId = task.task_id;
    renderTasks(state.tasks.map((candidate) => ({ ...candidate, selected: candidate.task_id === task.task_id })));
  }

  async function confirmSelection() {
    if (!state.selectedTaskId) return;
    try {
      let properties = {
        _customization_session_id: state.sessionId,
        _customization_task_id: state.selectedTaskId,
        _customization_confirmed: "true",
        _customization_locale: locale,
        _customization_catalog_id: state.product.id,
      };
      if (!isLocalDemo) {
        const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/added-to-cart`, {
          method: "POST",
          headers: authHeaders(),
          body: "{}",
        });
        properties = payload.line_item_properties;
      }
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        Object.entries(properties).forEach(([name, value]) => {
          let field = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
          if (!field) {
            field = document.createElement("input");
            field.type = "hidden";
            field.name = `properties[${name}]`;
            form.append(field);
          }
          field.value = value;
        });
      }
      nodes.dialog.close();
      setStatus(copy("product.cart", "Preview selected. Your customization is ready to add to cart."));
      nodes.generate.textContent = copy("preview.confirm", "Selected · Ready for cart");
      if (nodes.cartButton) nodes.cartButton.disabled = false;
    } catch (error) {
      setStatus(error.message, true);
    }
  }

  function showHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    nodes.historyList.innerHTML = saved
      ? `<article class="history-item"><div><strong>${saved.productHandle}</strong><br><small>${new Date(saved.createdAt).toLocaleString()}</small></div><button type="button" data-restore-session>Restore</button></article>`
      : `<p>${copy("product.history_empty", "No saved session on this device yet.")}</p>`;
    nodes.historyDialog.showModal();
  }

  async function restoreHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    if (!saved || saved.productHandle !== handle) return;
    state.sessionId = saved.sessionId;
    state.sessionToken = saved.sessionToken;
    if (isLocalDemo) {
      demoStart();
      nodes.historyDialog.close();
      nodes.dialog.showModal();
      return;
    }
    await refreshSession();
    nodes.historyDialog.close();
    nodes.dialog.showModal();
    beginPolling();
  }

  function bindEvents() {
    nodes.input.addEventListener("change", () => {
      const file = nodes.input.files?.[0];
      if (!file) return;
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
        setStatus(copy("product.upload_help", "Please choose a JPG, PNG or WEBP image under 10 MB."), true);
        return;
      }
      if (state.sourceObjectUrl) URL.revokeObjectURL(state.sourceObjectUrl);
      state.file = file;
      state.sourceObjectUrl = URL.createObjectURL(file);
      nodes.sourcePreview.src = state.sourceObjectUrl;
      nodes.sourcePreview.hidden = false;
      nodes.uploadCopy.hidden = true;
      updateGenerateAvailability();
      setStatus(isLocalDemo || state.turnstileToken
        ? copy("product.generate", "Photo ready. Generate four independent 2K previews when you are ready.")
        : copy("product.security", "Photo ready. Complete the security check to generate previews."));
    });
    document.addEventListener("change", (event) => {
      if (event.target.matches('input[name="style"]')) nodes.selectedStyle.textContent = currentStyle();
      if (event.target.matches('input[name="size"]')) nodes.selectedSize.textContent = event.target.value;
    });
    nodes.generate.addEventListener("click", startGeneration);
    nodes.previewGrid.addEventListener("click", (event) => {
      const retry = event.target.closest("[data-retry-slot]");
      const select = event.target.closest("[data-select-slot]");
      const card = event.target.closest("[data-slot]");
      if (retry) retrySlot(retry.dataset.retrySlot).catch((error) => setStatus(error.message, true));
      if (select) selectSlot(select.dataset.selectSlot).catch((error) => setStatus(error.message, true));
      if (card && !retry && !select) selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
    });
    nodes.previewGrid.addEventListener("keydown", (event) => {
      const card = event.target.closest("[data-slot]");
      if (card && ["Enter", " "].includes(event.key)) {
        event.preventDefault();
        selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
      }
    });
    nodes.stageRetry.addEventListener("click", () => {
      retrySlot(nodes.stageRetry.dataset.stageRetry).catch((error) => setStatus(error.message, true));
    });
    nodes.confirm.addEventListener("click", confirmSelection);
    document.querySelector("[data-preview-close]").addEventListener("click", () => nodes.dialog.close());
    document.querySelector("[data-generate-again]").addEventListener("click", () => {
      state.sessionId = "";
      state.sessionToken = "";
      state.batchKey = "";
      state.tasks = [];
      nodes.dialog.close();
      if (isLocalDemo) {
        startGeneration();
        return;
      }
      state.turnstileToken = "";
      if (window.turnstile && state.turnstileWidgetId !== "") window.turnstile.reset(state.turnstileWidgetId);
      updateGenerateAvailability();
      setStatus(copy("product.security", "Complete the security check again before generating a new set of four previews."));
    });
    document.querySelector("[data-history-button]").addEventListener("click", showHistory);
    document.querySelector("[data-history-close]").addEventListener("click", () => nodes.historyDialog.close());
    nodes.historyList.addEventListener("click", (event) => {
      if (event.target.closest("[data-restore-session]")) restoreHistory().catch((error) => setStatus(error.message, true));
    });
  }

  async function initialize() {
    state.dictionary = await loadDictionary();
    applyLocaleCopy();
    let catalog = window.FORM_OF_US_CATALOG;
    if (!catalog) {
      const response = await fetch("/catalog/products.json");
      catalog = await response.json();
    }
    const product = catalog.products.find((candidate) => candidate.handle === handle) || catalog.products[0];
    renderProduct(product);
    bindEvents();
    updateGenerateAvailability();
    const localeSelect = document.querySelector("[data-locale-select]");
    localeSelect.value = locale;
    localeSelect.addEventListener("change", () => {
      localStorage.setItem("formOfUsLocale", localeSelect.value);
      location.reload();
    });
    initializeTurnstile().catch((error) => {
      setStatus(error.message, true);
      updateGenerateAvailability();
    });
  }

  initialize().catch((error) => setStatus(error.message, true));
})();
